/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axiosInstance from '../../axios';
import nexo from '../../nexoClient';
import tokens from '@nimbus-ds/tokens/dist/js/tokens';
import { Page } from '@nimbus-ds/patterns';
import {
  Card,
  Box,
  Button,
  useToast,
  Table,
  Title,
  IconButton,
  Text,
} from '@nimbus-ds/components';
import { QuestionCircleIcon } from '@nimbus-ds/icons';
import { navigateHeader } from '@tiendanube/nexo/helpers';
import { useWindowWidth } from '../../hooks';

interface Subasta {
  id: number;
  name: string;
  handle: string;
  cantidad_ofertas: number;
  fecha_finalizacion: string;
  ganador_id: number | null;
  ganador: string | null;
  status: string; // "activo" | "finalizada"
}

const formatDate = (d: string) =>
  new Date(d).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatCountdown = (d: string, now: number) => {
  const ms = new Date(d).getTime() - now;
  if (ms <= 0) return '0 s';
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86_400);
  const hours = Math.floor((totalSec % 86_400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  return days >= 1
    ? `${days} d ${minutes} min ${seconds.toString().padStart(2, '0')} s`
    : `${hours} hs ${minutes} min ${seconds.toString().padStart(2, '0')} s`;
};

const ConfigPage: React.FC = () => {
  const { push } = useHistory();
  const windowWidth = useWindowWidth();
  const breakpointMd = tokens.breakpoint.md.value.replace('px', '');
  const { addToast } = useToast();
  const location = useLocation();

  const [activeSubastas, setActiveSubastas] = useState<Subasta[]>([]);
  const [endedSubastas, setEndedSubastas] = useState<Subasta[]>([]);
  const [now, setNow] = useState(Date.now());
  const [isButtonDisabled, setButtonDisabled] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    navigateHeader(nexo, { goTo: '/', text: 'Volver al inicio' });
  }, []);

  const fetchSubastas = async () => {
    try {
      const res = await axiosInstance.get('/auction_list');
      if (res.data?.status === 'SUCCESS') {
        const todas: Subasta[] = res.data.auctions || [];
        setActiveSubastas(todas.filter((s) => s.status === 'activo'));
        setEndedSubastas(todas.filter((s) => s.status === 'finalizada'));
      }
    } catch (err) {
      console.error(err);
      addToast({
        id: 'fetch-subastas-error',
        type: 'danger',
        text: 'Error al cargar subastas',
        duration: 4000,
      });
    }
  };

  useEffect(() => {
    fetchSubastas();
  }, []);

  const headersPendientes = [
    'Id',
    'Nombre',
    'Denominación',
    'Cantidad ofertas',
    'Tiempo restante',
    'Finalización',
    'Acción',
  ];
  const headersFinalizadas = [
    'Id',
    'Nombre',
    'Denominación',
    'Ofertas totales',
    'Ganador',
    'Finalización',
    'Acción',
  ];

  const actions =
    windowWidth !== null && windowWidth <= breakpointMd ? (
      <IconButton
        backgroundColor="transparent"
        borderColor="transparent"
        source={<QuestionCircleIcon />}
        size="2rem"
      />
    ) : (
      <Box display="flex" gap="4">
        <Button appearance="primary" onClick={fetchSubastas}>
          REFRESCAR
        </Button>
      </Box>
    );

  const onDetailSubastaView = (id: number) => {
    push(`/auction/${id}`);
  };

  return (
    <Page maxWidth="1300px">
      <Page.Header title="Subastas" buttonStack={actions} />
      <Page.Body>
        {/* Subastas en curso */}
        <Card>
          <Title fontSize="4">Subastas en curso</Title>
          <Table>
            <Table.Head>
              {headersPendientes.map((h) => (
                <Table.Cell key={h}>{h}</Table.Cell>
              ))}
            </Table.Head>
            <Table.Body>
              {activeSubastas.map((s) => (
                <Table.Row key={s.id}>
                  <Table.Cell>{s.id}</Table.Cell>
                  <Table.Cell>{s.name}</Table.Cell>
                  <Table.Cell>{s.handle}</Table.Cell>
                  <Table.Cell>{s.cantidad_ofertas}</Table.Cell>
                  <Table.Cell>
                    <Text fontWeight="bold" fontSize="highlight">
                      {formatCountdown(s.fecha_finalizacion, now)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>{formatDate(s.fecha_finalizacion)}</Table.Cell>
                  <Table.Cell>
                    <Button
                      onClick={() => onDetailSubastaView(s.id)}
                      appearance="primary"
                      disabled={isButtonDisabled}
                    >
                      Ver detalles
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Card>

        {/* Subastas finalizadas */}
        <Card>
          <Title fontSize="4">Subastas finalizadas</Title>
          <Table>
            <Table.Head>
              {headersFinalizadas.map((h) => (
                <Table.Cell key={h}>{h}</Table.Cell>
              ))}
            </Table.Head>
            <Table.Body>
              {endedSubastas.map((s) => (
                <Table.Row key={s.id}>
                  <Table.Cell>{s.id}</Table.Cell>
                  <Table.Cell>{s.name}</Table.Cell>
                  <Table.Cell>{s.handle}</Table.Cell>
                  <Table.Cell>{s.cantidad_ofertas}</Table.Cell>
                  <Table.Cell>
                    <Text fontWeight="bold">{s.ganador}</Text>
                  </Table.Cell>
                  <Table.Cell>{formatDate(s.fecha_finalizacion)}</Table.Cell>
                  <Table.Cell>
                    <Button
                      onClick={() => onDetailSubastaView(s.id)}
                      appearance="primary"
                      disabled={isButtonDisabled}
                    >
                      Ver detalles
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Card>
      </Page.Body>
    </Page>
  );
};

export default ConfigPage;
